import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Suspense, type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';

export interface QueryBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  compact?: boolean;
}

/**
 * Suspense plus an error boundary wired to react-query's reset, so "try again" actually refetches
 * instead of re-rendering the same rejected promise.
 */
export function QueryBoundary({
  children,
  fallback,
  compact = false,
}: QueryBoundaryProps): ReactNode {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ error, resetErrorBoundary }) => (
            <ErrorState error={error} onRetry={resetErrorBoundary} compact={compact} />
          )}
        >
          <Suspense fallback={fallback ?? <LoadingState compact={compact} />}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
